import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
    Box,
    Button,
    Divider,
    Grid,
    Paper,
    Tab,
    Tabs,
    Typography,
    CircularProgress,
    List,
    ListItem,
    IconButton,
    ListItemText,
    Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LaunchIcon from "@mui/icons-material/Launch";
import { TabContext, TabPanel } from "@mui/lab";

import { MetaDrivenTable, MapBox, Data, DataLayout, PaginationControls, DialogBox, DrawerControl } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";
import { useProject } from "../hooks/useProject";
import { useAssetDetails } from "../hooks/useAssetDetails";
import { useStakeHolder } from "../hooks/useStakeHolder";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { Asset, StakeHolder } from "../components";
import { AssetProps } from "../components/Asset";
import { useNavigation } from "../../../hooks/useNavigation";
import { DeleteConfirmation } from "../../materialRequests/components";
import { projectDetailsApi } from "../api/projectDetails";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { formatCurrency } from "../../../shared/utils";
import { clientTheme } from "../../../theme/clientTheme";
import { useScheduleEditor } from "../../schedulePlanning/hooks";
import { useStakeHolderActions } from "../hooks/useStakeHolderActions";
import { useStakeHolderOwnership } from "../hooks/useStakeHolerOwnership";
import { useStakeholderRedirection } from "../hooks/useStakeholderRedirection";

const hiddenProjectFormFields = clientTheme.layout?.hiddenProjectFormFields ?? [];
const hideProjectAssetDetails = clientTheme.layout?.hideProjectAssetDetails ?? false;

export const Overview: React.FC = () => {

    const { isAllowedToEditProject, isAllowedToAddStakeHolder, isAllowedToAddAsset, isAllowedToEditAsset, isAllowedToDeleteStakeHolder } = useCapabilities();
  
    const { goToEditProject } = useNavigation();

    const { projectId } = useParams<{ projectId: string }>();

    const { refetch } = useGetJeopardyStakeholders({ params: { project_id: projectId } });

    const{isEstimateValidationStageGateCompleted}= useScheduleEditor(Number(projectId));

    const { handleStakeholderRedirection } = useStakeholderRedirection();

    const [currentTab, setCurrentTab] = useState("details");

    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    const [isAddAsset, setIsAddAsset] = useState(false);
    const [isAddStakeHolder, setIsAddStakeHolder] = useState(false);

    const [isEditAsset, setIsEditAsset] = useState<{
        status: boolean;
        assetDetails: AssetProps | null;
    }>({
        status: false,
        assetDetails: null
    });

    const [isDeleteAsset, setIsDeleteAsset] = useState<{
        status: boolean;
        id: number | null;
    }>({
        status: false,
        id: null
    });

    const [isDeleteStakeHolder, setIsDeleteStakeHolder] = useState<{
        status: boolean;
        stakeHolderDetails: any | null;
    }>({
        status: false,
        stakeHolderDetails: null,
    });

    const [isReassignStakeHolder, setIsReassignStakeHolder] = useState<{
        status: boolean;
        stakeHolderDetails: any | null;
    }>({
        status: false,
        stakeHolderDetails: null,
    });

    const [isEditStakeHolder, setIsEditStakeHolder] = useState<{
        status: boolean;
        id: null | number;
    }>({
        status: false,
        id: null
    });

    const [deletingAsset, setDeletingAsset] = useState(false);
    const [deletingStakeHolder, setDeletingStakeHolder] = useState(false);

    const tabs = [
        { value: "details", label: "Project Details" },
        ...(!hideProjectAssetDetails ? [{ value: "assets", label: "Assets" }] : []),
        { value: "stake-holders", label: "StakeHolders" }
    ];

    const { isPending: isProjectDetailsPending, projectData, isError: isProjectDetailsError, error: projectDetailsError } = useProject(projectId);

    const { isPending: isAssestDetailsPending, assetData, assetDataPagination, changePage: changeAssetPage, changeRowsPerPage: changeAssetRowsPerPage, isRefetching: isAssetRefetching, refetch: refetchAsset, removeCache: removeAssetCache } = useAssetDetails(projectId);
    
    const { isPending: isStakeHolderDetailsPending, stakeHolderData, stakeHolderDataPagination, changePage: changeStakeHolderPage, changeRowsPerPage: changeStakeHolderRowsPerPage, isRefetching: isStakeHolderRefetching, refetch: refetchStakeHolder } = useStakeHolder({ projectId, isRemovePagination: false });
    
    const stakeHolderActions = useStakeHolderActions({ setIsDeleteStakeHolder, setIsReassignStakeHolder });
    
    const { ownershipData, ownershipCount, isPending: isOwnershipPending, refetch: refetchOwnership } = useStakeHolderOwnership({ 
        projectId,
        userId: isReassignStakeHolder.stakeHolderDetails?.user_id || null 
    });

    useEffect(() => {
        if (isReassignStakeHolder.status && isReassignStakeHolder.stakeHolderDetails?.user_id) {
            refetchOwnership?.();
        }
    }, [isReassignStakeHolder.status, isReassignStakeHolder.stakeHolderDetails, refetchOwnership]);

    const [deleting, setDeleting] = useState(false);

    const assetHeaders = [
        { "alias_name": "asset_category_display_text", "label": "Asset Category", "domain": "", "display_order": 1, "is_show": true },
        { "alias_name": "narms_value", "label": "NARMS Values", "domain": "", "display_order": 2, "is_show": true },
        { "alias_name": "iln_description", "label": "Hcode", "domain": "", "display_order": 3, "is_show": !hiddenProjectFormFields.includes("hc_code") },
        { "alias_name": "asset_type_display_text", "label": "Asset Type", "domain": "", "display_order": 4, "is_show": true },
        { "alias_name": "programme_name", "label": "Programme", "domain": "", "display_order": 5, "is_show": true },
        { "alias_name": "functional_location", "label": "Functional Location", "domain": "", "display_order": 6, "is_show": true },
        { "alias_name": "asset_budget", "label": "Asset Budget", "domain": "", "display_order": 7, "is_show": true },
        { "alias_name": "asset_description", "label": "Asset", "domain": "", "display_order": 8, "is_show": true },
        { "alias_name": "sap_hierarchy", "label": "SAP Hierarchy", "domain": "", "display_order": 9, "is_show": true },
    ];

    const stakeHolderHeaders = [
        { "alias_name": "full_name", "label": "Name", "domain": "PROJECT_STATUS", "display_order": 1, "is_show": true },
        { "alias_name": "email", "label": "Email", "domain": "PROJECT_STATUS", "display_order": 2, "is_show": true },
        { "alias_name": "role", "label": "Role", "domain": "", "display_order": 3, "is_show": true },
    ];

    const Loader = () => {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    const handleDeleteAsset = async () => {
        if (!isDeleteAsset.id) return;
        setDeletingAsset(true);
        try {
            await projectDetailsApi.deleteAsset({ projectId, assetId: isDeleteAsset.id });
            removeAssetCache();
            setIsDeleteAsset({ status: false, id: null });
            refetchAsset();
        } finally {
            setDeletingAsset(false);
        }
    };

    const handleDelete = async () => {
        if (!isDeleteStakeHolder.stakeHolderDetails) return;

        setDeletingStakeHolder(true);

        const response = await projectDetailsApi.deleteStakeHolder({ projectId, stakeHolderId: isDeleteStakeHolder.stakeHolderDetails.id });
        if (response.status) {
            setIsDeleteStakeHolder({ status: false, stakeHolderDetails: null });
            refetchStakeHolder();
            refetch();
        }
        
        setDeletingStakeHolder(false);
    };

    const formatType = (type: string) => {
      return type.charAt(0).toUpperCase() + type.slice(1);
    };

    if (isProjectDetailsPending || isAssestDetailsPending || isStakeHolderDetailsPending) {
        return (
            <Loader />
        )
    };

    if (isProjectDetailsError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <Typography variant="h6" color="error">Error fetching project details</Typography>
                <Typography variant="body2" color="text.secondary">{projectDetailsError?.message}</Typography>
                <Button variant="contained" onClick={() => refetch()}>Retry</Button>
            </Box>
        )
    };

    return (
        <Paper
            data-background-elevation={1}
            sx={{ height: "100%", boxShadow: 'none', borderRadius: 0 }}
        >

            <Grid container spacing={2} p={2} height="100%">

                {/* LEFT PANEL */}
                <Grid size={3}>
                    <Paper
                        data-background-elevation={1}
                        sx={{ overflow: "hidden", height: "fit-content" }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                height: { xs: 350, md: 300 },
                                minHeight: 350,
                            }}
                        >
                            {
                                !projectData.coordinates?.coordinates ||
                                    (projectData.coordinates.coordinates[0] === 0 && projectData.coordinates.coordinates[1] === 0) ?
                                    <Box
                                        height="100%"
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        width="100%"
                                        gap={2}
                                        p={5}
                                        border="1px dashed"
                                        borderColor="text.secondary"
                                        borderRadius={1}
                                    >
                                        <Typography variant="body1" color="text.secondary">No location data available for this project.</Typography>
                                    </Box>
                                    : <MapBox location={projectData.coordinates.coordinates} />
                            }
                        </Box>
                    </Paper>

                    <Box display={"flex"} flexDirection={"column"} gap={1.2} mt={2}>

                        <Typography variant="body1" color="text.primary">
                            {projectData.title}
                        </Typography>

                        <Divider />

                        {/* Reference and Location Info */}
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.primary">Project Reference</Typography>
                            <Typography variant="body2" color="text.primary">{projectData.project_reference}</Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: { xs: "wrap", md: "nowrap" },
                                justifyContent: "space-between",
                            }}
                            gap={2}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {projectData.address ? projectData.address : "Location not specified"}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ flexShrink: 0 }}
                            >
                                {projectData.area_text ? projectData.area_text : 'N/A'}
                            </Typography>
                        </Box>

                        <Divider />

                        {[
                            { key: "description", label: "Description", content: projectData.description },
                            // { key: "functional_location", label: "Functional Location", content: projectData.functional_location },
                            { key: "project_scope", label: "Project Scope", content: projectData.project_scope },
                            // { key: "stage_description", label: "Stage Description", content: projectData.stage_description },
                            { key: "reporting_year", label: "Reporting Year", content: projectData.project_year },
                            { key: "status_display_text", label: "Project Status", content: projectData.status_display_text },
                            { key: "work_type_description", label: "Work Type", content: projectData.work_type_description },
                            { key: "work_type_group_description", label: "Work Type Group", content: projectData.work_type_group_description },
                        ].map((item, index) => (
                            <Data key={index} label={item.label} content={item.content} />
                        ))}

                    </Box>
                </Grid>

                {/* RIGHT PANEL */}
                <Grid size={9}>
                    <Box display="flex" flexDirection="column" height="100%">

                        <TabContext value={currentTab}>
                            <Box display="flex" alignItems="center" alignContent="center">
                                <Tabs
                                    value={currentTab}
                                    onChange={handleChangeTab}
                                    aria-label="Tabs"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    allowScrollButtonsMobile
                                    sx={{ minHeight: "auto" }}
                                >
                                    {tabs.map((t) => (
                                        <Tab
                                            key={t.value}
                                            sx={{ height: "auto", minHeight: "auto" }}
                                            label={t.label}
                                            value={t.value}
                                        />
                                    ))}
                                </Tabs>
                                <Box display={'flex'} alignItems={'center'} gap={1} ml="auto">
                                    {
                                        (currentTab === "details" && isAllowedToEditProject) && (
                                            <Button variant="text" onClick={() => { projectId && goToEditProject(projectId) }}>
                                                Edit project
                                            </Button>
                                        )
                                    }
                                    {
                                        (currentTab === "stake-holders" && isAllowedToAddStakeHolder) && (
                                            <Button variant="text" onClick={() => setIsAddStakeHolder(true)}>
                                                Add StakeHolder
                                            </Button>
                                        )
                                    }
                                    {
                                        (currentTab === "assets" && isAllowedToAddAsset) && (
                                            <Button variant="text" onClick={() => setIsAddAsset(true)}>
                                                Add Asset
                                            </Button>
                                        )
                                    }
                                </Box>
                            </Box>
                            <Divider />
                            <Box flex={1} overflow="hidden" mt={2}>
                                <TabPanel value="details" sx={{ p: 0, height: "100%" }}>
                                    <Box px={1}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <DataLayout title="Details">
                                                    <Box display="flex" flexDirection="column" gap={1.5} p={2}>
                                                        {[
                                                            { key: "project_budget", label: "Project Budget", content: formatCurrency(projectData.project_budget) },
                                                            { key: "project_income", label: "Project Income", content: formatCurrency(projectData.project_income)},
                                                            { key: "description", label: "Description", content: projectData.description },
                                                            { key: "project_scope", label: "Project Scope", content: projectData.project_scope },
                                                            { key: "workstream", label: "Workstream", content: projectData.workstream_description },
                                                            { key: "portfolio", label: "Portfolio", content: projectData.portfolio_name },
                                                            { key: "programme", label: "Programme", content: projectData.programme_reference },
                                                        ].map((item, index) => (
                                                            <Data key={index} label={item.label} content={item.content} />
                                                        ))}
                                                    </Box>
                                                </DataLayout>
                                            </Grid>
                                            <Grid size={6}>
                                                <DataLayout title="Location Details">
                                                    <Box display="flex" flexDirection="column" gap={1.5} p={2}>
                                                        {[
                                                            { key: "site", label: "Site", content: projectData.site_description },
                                                            { key: "address", label: "Address", content: projectData.address },
                                                            { key: "what3_words", label: "What3Words", content: projectData.what3_words },
                                                            { key: "network", label: "Network", content: projectData.network_description },
                                                            ...(!hiddenProjectFormFields.includes("g17") ? [{ key: "g_17", label: "G17", content: projectData.g_17 }] : []),
                                                        ].map((item, index) => (
                                                            <Data key={index} label={item.label} content={item.content} />
                                                        ))}
                                                    </Box>
                                                </DataLayout>
                                            </Grid>
                                            <Grid size={6}>
                                                <DataLayout title="Schedule Details">
                                                    <Box display="flex" flexDirection="column" gap={1.5} p={2}>
                                                        {[
                                                            { key: "planned_start_date", label: "Planned Start Date", content: formatDate(projectData.planned_start_date) },
                                                            { key: "planned_end_date", label: "Planned End Date", content: formatDate(projectData.planned_end_date) },
                                                            { key: "planned_commissioning_date", label: "Planned Commissioning Date", content: formatDate(projectData.planned_commissioning_date) },
                                                            { key: "planned_close_up_date", label: " Planned Close Out Date", content: formatDate(projectData.planned_close_up_date) },
                                                        ].map((item, index) => (
                                                            <Data key={index} label={item.label} content={item.content} />
                                                        ))}
                                                    </Box>
                                                </DataLayout>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </TabPanel>
                                <TabPanel value="assets" sx={{ p: 0, height: "100%" }}>
                                    {
                                        isAssetRefetching ?
                                            <Loader />
                                            :
                                            <Paper sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                                                <MetaDrivenTable
                                                    headers={assetHeaders}
                                                    data={assetData}
                                                    dataMetrics={[]}
                                                    sortDisabled={true}
                                                    actions={isAllowedToEditAsset ? [
                                                        {
                                                            label: <EditIcon />,
                                                            handleClick: (row: AssetProps) => {
                                                                setIsEditAsset({
                                                                    status: true,
                                                                    assetDetails: row
                                                                });
                                                            }
                                                        },
                                                        ...(!isEstimateValidationStageGateCompleted ? [{
                                                            label: <DeleteIcon />,
                                                            handleClick: (row: AssetProps) => {
                                                                setIsDeleteAsset({
                                                                    status: true,
                                                                    id: row.asset_id
                                                                });
                                                            }
                                                        }] : [])
                                                    ] : []}


                                                />
                                                <PaginationControls
                                                    total={assetDataPagination?.total}
                                                    rowsPerPage={assetDataPagination?.limit}
                                                    currentPage={assetDataPagination?.page}
                                                    handlePageChange={changeAssetPage}
                                                    handleRowsPerPage={changeAssetRowsPerPage}
                                                />
                                            </Paper>
                                    }
                                </TabPanel>
                                <TabPanel value="stake-holders" sx={{ p: 0, height: "100%" }}>
                                    {
                                        isStakeHolderRefetching ?
                                            <Loader />
                                            :
                                            <Paper sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                                                <MetaDrivenTable
                                                    headers={stakeHolderHeaders}
                                                    data={stakeHolderData}
                                                    dataMetrics={{ "created_at": { type: "date" } }}
                                                    sortDisabled={true}
                                                    actions={stakeHolderActions}
                                                />
                                                <PaginationControls
                                                    total={stakeHolderDataPagination?.total}
                                                    rowsPerPage={stakeHolderDataPagination?.limit}
                                                    currentPage={stakeHolderDataPagination?.page}
                                                    handlePageChange={changeStakeHolderPage}
                                                    handleRowsPerPage={changeStakeHolderRowsPerPage}
                                                />
                                            </Paper>
                                    }
                                </TabPanel>
                            </Box>
                        </TabContext>
                    </Box>
                </Grid>

            </Grid>

            <DrawerControl
                isOpen={isEditAsset.status}
                title="Edit Asset"
                subTitle=""
                handleClose={() => setIsEditAsset({ status: false, assetDetails: null })}
                children={
                    isEditAsset.assetDetails && projectId &&
                    <Asset
                        projectId={projectId}
                        asset={isEditAsset.assetDetails}
                        onSuccess={() => {
                            refetchAsset();
                            setIsEditAsset({ status: false, assetDetails: null });
                        }}
                        onClose={() => setIsEditAsset({ status: false, assetDetails: null })}
                        isEdit={true}
                    />
                }
            />

            <DrawerControl
                isOpen={isAddAsset}
                title="Add Asset"
                subTitle=""
                handleClose={() => setIsAddAsset(false)}
                children={
                    projectId &&
                    <Asset
                        projectId={projectId}
                        onSuccess={() => {
                            setIsEditAsset({ status: false, assetDetails: null });
                            setIsAddAsset(false);
                            refetchAsset();
                        }}
                        onClose={() => setIsAddAsset(false)}
                        isEdit={false}
                    />
                }
            />

            <DialogBox
                showModal={isAddStakeHolder}
                title={"Add StakeHolder"}
                subTitle="Please fill in the details to add a new stakeholder to this project."
                onModalClose={() => {
                    setIsAddStakeHolder(false);
                }}
                children={
                    <StakeHolder
                        projectId={projectId}
                        onSuccess={() => {
                            setIsAddStakeHolder(false);
                            refetchStakeHolder();
                            refetch();
                        }}
                        existingStakeHolders={stakeHolderData.map((s: { user_id: any; }) => s.user_id)}
                        onClose={() => setIsAddStakeHolder(false)}
                    />
                }
            />

            <DialogBox
                showModal={isDeleteAsset.status}
                title={"Delete Asset"}
                subTitle=""
                onModalClose={() => {
                    setIsDeleteAsset({ status: false, id: null });
                }}
                children={
                    <DeleteConfirmation
                        deleting={deletingAsset}
                        message="Are you sure you want to delete this asset? This action cannot be undone."
                        onConfirm={() => {
                            handleDeleteAsset();
                        }}
                        onCancel={() => {
                            setIsDeleteAsset({ status: false, id: null });
                        }}
                    />
                }
            />

            <DialogBox
                showModal={isDeleteStakeHolder.status}
                title={"Delete StakeHolder"}
                subTitle={""}
                onModalClose={() => {
                    setIsDeleteStakeHolder({ status: false, stakeHolderDetails: null });
                }}
                children={
                    <DeleteConfirmation
                        deleting={deletingStakeHolder}
                        message="Are you sure you want to delete this stakeholder? This action cannot be undone."
                        onConfirm={() => {
                            handleDelete();
                        }}
                        onCancel={() => {
                            setIsDeleteStakeHolder({ status: false, stakeHolderDetails: null });
                        }}
                    />
                }
            />

            <DrawerControl
                isOpen={isReassignStakeHolder.status}
                title="Reassign StakeHolder"
                subTitle={`${ownershipCount} task(s) assigned to ${isReassignStakeHolder.stakeHolderDetails?.full_name}`}
                handleClose={() => setIsReassignStakeHolder({ status: false, stakeHolderDetails: null })}
                children={
                    isOwnershipPending ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress />
                        </Box>
                ) : (
                        <List>
                            {ownershipData?.map((item: any) => (
                                <ListItem
                                    key={`${item.object_type}-${item.object_id}`}
                                    secondaryAction={
                                        <Tooltip title={`View ${item.object_type} details`}>
                                            <span>
                                                <IconButton onClick={() => handleStakeholderRedirection(item)} edge="end" aria-label="details">
                                                    <LaunchIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                          <Typography variant="body2" fontWeight={500}>
                                            {item.description}
                                          </Typography>
                                        }
                                        secondary={
                                          <Typography variant="caption" color="text.secondary">
                                            {formatType(item.object_type)}
                                          </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )
                }
            />

        </Paper>
    );
};